import { computed, shallowRef, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useNuxtApp, useRuntimeConfig } from '#app'
import { logIterableError, type IterableLogExtras } from '~/utils/iterable-log'

/** Runtime shape of `nuxt/plugins/iterable.client.ts` provide (for typing after null check). */
interface IterablePluginRuntime {
  setEmail: (email: string) => Promise<string>
  setUserID: (userId: string) => Promise<string>
  logout: () => void
  embeddedManager: unknown
  embeddedMessages: Ref<unknown[]>
  embeddedMessagesByPlacement: Ref<Record<number, unknown[]>>
  syncEmbeddedMessages: (placementIds?: number[]) => Promise<unknown>
  startImpression: (messageId: string, placementId: number) => void
  pauseImpression: (messageId: string) => void
  packageName: string
  identityReady: Ref<boolean>
  hasIterableIdentity: Ref<boolean>
  cleanupPageFinishDebounce: () => void
}

function getRawIterable () {
  const { $iterable } = useNuxtApp()
  if (!$iterable) {
    return {
      isEnabled: false,
      setEmail: () => Promise.resolve(),
      setUserID: () => Promise.resolve(),
      logout: () => {},
      embeddedManager: null,
      embeddedMessages: shallowRef([]),
      embeddedMessagesByPlacement: shallowRef({}),
      syncEmbeddedMessages: () => Promise.resolve(),
      startImpression: () => {},
      pauseImpression: () => {},
      /** Align with plugin provide shape when SDK is absent */
      packageName: '',
      identityReady: shallowRef(false),
      hasIterableIdentity: shallowRef(false),
      cleanupPageFinishDebounce: () => {}
    }
  }
  const iterablePlugin = $iterable as IterablePluginRuntime
  return {
    isEnabled: true,
    setEmail: iterablePlugin.setEmail,
    setUserID: iterablePlugin.setUserID,
    logout: iterablePlugin.logout,
    embeddedManager: iterablePlugin.embeddedManager,
    embeddedMessages: iterablePlugin.embeddedMessages,
    embeddedMessagesByPlacement: iterablePlugin.embeddedMessagesByPlacement,
    syncEmbeddedMessages: iterablePlugin.syncEmbeddedMessages,
    startImpression: iterablePlugin.startImpression,
    pauseImpression: iterablePlugin.pauseImpression,
    packageName: iterablePlugin.packageName,
    identityReady: iterablePlugin.identityReady ?? shallowRef(false),
    hasIterableIdentity: iterablePlugin.hasIterableIdentity ?? shallowRef(false),
    cleanupPageFinishDebounce: iterablePlugin.cleanupPageFinishDebounce ?? (() => {})
  }
}

function iterableExtras (routePath: string, placementId?: number): IterableLogExtras {
  return {
    route: routePath,
    ...(placementId != null ? { placementId } : {})
  }
}

export function useIterable () {
  const iterableBinding = getRawIterable()
  const config = useRuntimeConfig()
  const iterableEnabled = Boolean(config.public.iterableEnabled)
  const route = useRoute()
  const routePath = () => (route?.fullPath != null ? String(route.fullPath) : '')

  /**
   * Reactive: `$iterable` attached and SDK usable. Use **`.value`** in script `computed` / functions
   * (templates auto-unwrap). Avoid destructuring to a boolean — that freezes the first snapshot.
   */
  const isEnabled = computed(() => getRawIterable().isEnabled)

  /**
   * Client-only: `true` when env expects Iterable (`iterableEnabled`) but the plugin did not attach the SDK
   * (import/load/init failure). Does not throw or change layout — use for dev logging or optional UI.
   * `false` when Iterable is off by env, or when the SDK is up. Not for JWT/identity errors (see `identityReady` / `hasIterableIdentity`).
   */
  const iterableSdkInitFailed = computed(() => {
    if (!import.meta.client || !iterableEnabled) return false
    return !getRawIterable().isEnabled
  })

  return {
    /** Env indicates Iterable Web SDK should load (`ITERABLE_WEB_API_KEY` set). */
    iterableEnabled,
    isEnabled,
    iterableSdkInitFailed,
    embeddedManager: iterableBinding.embeddedManager,
    embeddedMessages: iterableBinding.embeddedMessages,
    embeddedMessagesByPlacement: iterableBinding.embeddedMessagesByPlacement,
    packageName: iterableBinding.packageName,
    identityReady: iterableBinding.identityReady,
    hasIterableIdentity: iterableBinding.hasIterableIdentity,
    cleanupPageFinishDebounce: iterableBinding.cleanupPageFinishDebounce,

    setEmail (email: string) {
      const iterableRuntime = getRawIterable()
      if (!iterableRuntime.isEnabled || !iterableRuntime.setEmail) return Promise.resolve()
      return Promise.resolve(iterableRuntime.setEmail(email)).catch((err) => {
        logIterableError('setEmail', err, iterableExtras(routePath()))
      }) as Promise<string>
    },

    setUserID (userId: string) {
      const iterableRuntime = getRawIterable()
      if (!iterableRuntime.isEnabled || !iterableRuntime.setUserID) return Promise.resolve()
      return Promise.resolve(iterableRuntime.setUserID(userId)).catch((err) => {
        logIterableError('setUserID', err, iterableExtras(routePath()))
      }) as Promise<string>
    },

    logout () {
      const iterableRuntime = getRawIterable()
      if (!iterableRuntime.isEnabled) return
      try {
        iterableRuntime.logout()
      } catch (err) {
        logIterableError('logout', err, iterableExtras(routePath()))
      }
    },

    syncEmbeddedMessages (placementIds?: number[]) {
      const iterableRuntime = getRawIterable()
      if (!iterableRuntime.isEnabled || !iterableRuntime.syncEmbeddedMessages) return Promise.resolve()
      if (placementIds?.length) {
        const validPlacementIds = placementIds.filter((id) => id != null && Number(id) > 0)
        if (validPlacementIds.length === 0) return Promise.resolve()
      }
      return Promise.resolve(iterableRuntime.syncEmbeddedMessages(placementIds)).catch((err) => {
        logIterableError('syncEmbeddedMessages', err, iterableExtras(routePath()))
      })
    },

    startImpression (messageId: string, placementId: number) {
      const iterableRuntime = getRawIterable()
      if (!iterableRuntime.isEnabled) return
      if (!messageId?.trim()) return
      try {
        iterableRuntime.startImpression(messageId, placementId)
      } catch (err) {
        logIterableError('startImpression', err, iterableExtras(routePath(), placementId))
      }
    },

    pauseImpression (messageId: string) {
      const iterableRuntime = getRawIterable()
      if (!iterableRuntime.isEnabled) return
      if (!messageId?.trim()) return
      try {
        iterableRuntime.pauseImpression(messageId)
      } catch (err) {
        logIterableError('pauseImpression', err, iterableExtras(routePath()))
      }
    }
  }
}
